import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Droplets, Pencil, Trash2, Plus, Eye, EyeOff, Star, ImagePlus, X, Loader2 } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import { formatJMD } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const blank = {
  name: "", size: "", type: "purchase", price_jmd: "", description: "",
  popular: false, badge: "", stock_status: "in_stock", image_url: "",
};

export const ProductsTab = ({ products, reload }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/product-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, price_jmd: String(p.price_jmd), badge: p.badge || "", image_url: p.image_url || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.size || !form.price_jmd) {
      toast.error("Name, size and price are required");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name, size: form.size, type: form.type,
      price_jmd: Number(form.price_jmd), description: form.description,
      popular: form.popular, badge: form.badge || null, stock_status: form.stock_status,
      image_url: form.image_url || null,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated — live on site");
      } else {
        await api.post("/products", payload);
        toast.success("Product added — live on site");
      }
      setOpen(false);
      reload();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/products/${p.id}/toggle`);
      toast.success(p.active ? "Hidden from site" : "Shown on site");
      reload();
    } catch { toast.error("Failed"); }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product removed");
      reload();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-500">Edits here update the public website instantly.</p>
        <Button data-testid="admin-btn-add-product" onClick={openNew} className="rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Add Bottle / Size
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-products-list">
        {products.map((p) => (
          <div key={p.id} className={`bg-white rounded-2xl border p-5 ${p.active ? "border-slate-200" : "border-dashed border-slate-300 opacity-60"}`}>
            <div className="flex items-start justify-between">
              {p.image_url ? (
                <img src={`${BACKEND_URL}${p.image_url}`} alt={p.name} className="w-14 h-14 rounded-xl object-cover border border-slate-200" data-testid={`product-thumb-${p.id}`} />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                  <Droplets className="w-5 h-5 text-sky-700" />
                </div>
              )}
              <div className="flex gap-1">
                <Badge variant="outline" className="text-[10px] capitalize">{p.type}</Badge>
                {p.popular && <Badge className="bg-sky-100 text-sky-700 border border-sky-200 text-[10px]"><Star className="w-2.5 h-2.5 mr-0.5" />Popular</Badge>}
              </div>
            </div>
            <h4 className="font-display font-bold text-slate-900 mt-3">{p.name}</h4>
            <div className="text-xs text-sky-600 font-semibold">{p.size}</div>
            <div className="font-mono-cw text-2xl font-extrabold text-slate-900 mt-2">{formatJMD(p.price_jmd)}</div>
            {p.stock_status !== "in_stock" && <span className="text-xs text-rose-500 font-semibold">Out of stock</span>}

            <div className="flex items-center gap-1 mt-4 pt-3 border-t border-slate-100">
              <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`} className="h-8 text-slate-600 text-xs">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="ghost" onClick={() => toggleActive(p)} data-testid={`product-toggle-${p.id}`} className="h-8 text-slate-600 text-xs">
                {p.active ? <><EyeOff className="w-3.5 h-3.5 mr-1" />Hide</> : <><Eye className="w-3.5 h-3.5 mr-1" />Show</>}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" data-testid={`product-delete-${p.id}`} className="h-8 text-rose-500 text-xs ml-auto">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {p.name}?</AlertDialogTitle>
                    <AlertDialogDescription>This removes it from the public website. This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(p)} className="bg-rose-600 hover:bg-rose-500" data-testid={`product-delete-confirm-${p.id}`}>Remove</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Product image</Label>
              <div className="mt-1 flex items-center gap-3">
                {form.image_url ? (
                  <div className="relative">
                    <img src={`${BACKEND_URL}${form.image_url}`} alt="Product" className="w-20 h-20 rounded-xl object-cover border border-slate-200" data-testid="product-form-image-preview" />
                    <button type="button" onClick={() => setForm({ ...form, image_url: "" })} data-testid="product-form-image-remove" className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-300"><ImagePlus className="w-6 h-6" /></div>
                )}
                <div>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid="product-form-image-input" onChange={(e) => uploadImage(e.target.files?.[0])} />
                  <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="product-form-image-upload" className="rounded-xl">
                    {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1" />} {form.image_url ? "Replace image" : "Upload image"}
                  </Button>
                  <p className="text-[11px] text-slate-400 mt-1">JPG, PNG or WEBP · max 5MB</p>
                </div>
              </div>
            </div>
            <div><Label>Name</Label><Input data-testid="product-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 rounded-xl" placeholder="e.g. 1 Litre Active Bottle" /></div>
            <div><Label>Size / Pack</Label><Input data-testid="product-form-size" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="h-10 rounded-xl" placeholder="e.g. 1 Litre (Case of 12)" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="product-form-type" className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="purchase">Purchase</SelectItem><SelectItem value="refill">Refill</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Price (JMD)</Label><Input type="number" data-testid="product-form-price" value={form.price_jmd} onChange={(e) => setForm({ ...form, price_jmd: e.target.value })} className="h-10 rounded-xl" /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Stock</Label>
                <Select value={form.stock_status} onValueChange={(v) => setForm({ ...form, stock_status: v })}>
                  <SelectTrigger className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="in_stock">In stock</SelectItem><SelectItem value="out_of_stock">Out of stock</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Badge (optional)</Label><Input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className="h-10 rounded-xl" placeholder="Most Popular" /></div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
              <Label className="cursor-pointer">Highlight as popular</Label>
              <Switch checked={form.popular} onCheckedChange={(v) => setForm({ ...form, popular: v })} data-testid="product-form-popular" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="product-form-save" onClick={save} disabled={saving} className="rounded-xl bg-sky-600 text-white">
              {editing ? "Save Changes" : "Add Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
